/**
 * Ritual Completed Notification Component
 * Displays a notification when a ritual is marked as completed by admin
 */

import { EllipsisVertical } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { getRitualTypeLabel } from "@/features/rituals/types"
import { formatDuration } from "@/features/rituals/utils/formatDuration"
import { getAssetUrl } from "@/utils/assets"
import { formatNotificationTime } from "@/utils/formatTime"
import type { Notification } from "../services/notification.service"
import { RitualNotificationCard } from "./RitualNotificationCard"

interface RitualCompletedNotificationProps {
  notification: Notification
  onRead?: () => void
  onUnread?: () => void
  onNotificationClick?: (notification: Notification) => void
}

interface RitualCompletedNotificationData {
  ritualId?: string
  ritualName?: string
  ritualCoverImageUrl?: string | null
  communityId?: string
  communityName?: string
  isPaid?: boolean
  fee?: number | null
  ritualType?: string
  duration?: string
}

export function RitualCompletedNotification({
  notification,
  onRead,
  onUnread,
  onNotificationClick,
}: RitualCompletedNotificationProps) {
  const data = notification.data as RitualCompletedNotificationData

  const ritualId = data.ritualId
  const ritualName = data.ritualName || "a ritual"
  const ritualCoverImageUrl = data.ritualCoverImageUrl
  const isRead = notification.isRead

  const relativeTime = formatNotificationTime(notification.createdAt)

  const handleMarkAsRead = () => {
    onRead?.()
  }

  const handleMarkAsUnread = () => {
    onUnread?.()
  }

  const handleUserClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (notification.triggeredByUser?.id) {
      window.location.href = `/profile/${notification.triggeredByUser.id}`
    }
  }

  const handleRitualClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (ritualId) {
      window.location.href = `/rituals/${ritualId}`
    }
  }

  const handleCommunityClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (data.communityId) {
      window.location.href = `/communities/${data.communityId}`
    }
  }

  const renderMessage = () => {
    const action = notification.action
    const triggeredBy = notification.triggeredByUser
    const userName = triggeredBy?.name || triggeredBy?.username || "Someone"

    if (action === "ritual_completed") {
      return (
        <>
          <button
            onClick={handleUserClick}
            className="font-semibold hover:underline cursor-pointer bg-transparent border-none p-0 text-current"
            type="button"
          >
            {userName}
          </button>
          {" marked "}
          <span className="font-semibold">{ritualName}</span>
          {" as completed"}
        </>
      )
    }

    if (action === "community_added_to_ritual") {
      const communityName = data.communityName || ""
      return (
        <>
          <button
            onClick={handleUserClick}
            className="font-semibold cursor-pointer bg-transparent border-none p-0 text-current"
            type="button"
          >
            {userName}
          </button>
          {" added "}
          <button
            onClick={handleRitualClick}
            className="font-semibold cursor-pointer bg-transparent border-none p-0 text-current"
            type="button"
          >
            {ritualName}
          </button>
          {" ritual to "}
          <button
            onClick={handleCommunityClick}
            className="font-semibold cursor-pointer bg-transparent border-none p-0 text-current"
            type="button"
          >
            {communityName}
          </button>
          {" community."}
        </>
      )
    }

    return notification.message
  }

  return (
    <div
      className={`flex flex-col gap-3 p-4 rounded-md cursor-pointer transition-colors border ${
        !isRead ? "notification-unread" : "notification-read"
      }`}
      onClick={() => {
        onRead?.()
        onNotificationClick?.(notification)
      }}
    >
      {/* Header Row: Logo, Brand, Time, Menu */}
      <div className="relative flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {/* Logo */}
          <img
            src={getAssetUrl("Avatar.png")}
            alt="Rituality"
            className="w-12 h-12 rounded-full object-cover"
          />

          {/* Brand Name and Message */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-primary text-[14px]">Rituality</span>
              <span className="text-stone text-[14px]">@Rituality</span>
              <span className="text-stone/50 text-[14px]">{relativeTime}</span>
            </div>

            {/* Message Row */}
            <div className="text-[14px] text-primary leading-[25px] pr-20">{renderMessage()}</div>
          </div>
        </div>

        {/* Menu */}
        <div className="absolute -top-2 -right-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                onClick={e => e.stopPropagation()}
              >
                <EllipsisVertical className="w-4 h-4 text-stone" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              {isRead ? (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleMarkAsUnread()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as unread
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleMarkAsRead()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as read
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <RitualNotificationCard
        coverImageUrl={ritualCoverImageUrl}
        title={ritualName}
        isPaid={data.isPaid}
        fee={data.fee}
        ritualTypeLabel={data.ritualType ? getRitualTypeLabel(data.ritualType) : undefined}
        duration={data.duration ? formatDuration(data.duration) : undefined}
      />
    </div>
  )
}
